import type { Database } from "../../__definitions__/database-definitions";

export type Assistant = Database["public"]["Tables"]["mcp_assistant_setup"]["Row"]["assistant"];

export type SetupField = { label: string; value: string } | { label: string; connectionUrl: true };

interface SetupStep {
  text: string;
  role?: "Admin" | "Setup owner";
  fields?: SetupField[];
}

interface SetupPath {
  id: string;
  label: string;
  steps: SetupStep[];
}

interface StarterPrompt {
  id: string;
  kind: string;
  text: string;
  claude: string;
}

export interface AssistantInfo {
  id: Assistant;
  name: string;
  plan: string;
  logo: string;
  removePath: string;
  paths: SetupPath[];
  tryIt: string;
  prompt: (prompt: StarterPrompt) => string;
}

const description = "Create, schedule and analyze employee comms";

const claudeDetails: SetupField[] = [
  { label: "Name", value: "ChangeEngine" },
  { label: "MCP server URL", connectionUrl: true },
];

const claudeToolPermissions: SetupField[] = [{ label: "Read-only tools", value: "Always allow" }];

const claudeStartChat =
  "Start a chat, check ChangeEngine is switched on under + › Connectors, and mention ChangeEngine in your request.";

const chatgptDetails: SetupField[] = [
  { label: "Name", value: "ChangeEngine" },
  { label: "Description", value: description },
  { label: "Server URL", connectionUrl: true },
  { label: "Authentication", value: "OAuth" },
];

const chatgptCreatePlugin = (role: SetupStep["role"]): SetupStep[] => [
  {
    text: "Enter these details. Leave Connection set to Server URL.",
    fields: chatgptDetails,
    role,
  },
  { text: "Tick I understand and want to continue, then click Create as a plugin.", role },
  {
    text: "In the Connect ChangeEngine window, click Continue to ChangeEngine and sign in with your ChangeEngine account.",
    role,
  },
  { text: "Review the tools and details ChatGPT found on the ChangeEngine server.", role },
  {
    text: "Under Personal › Created by you, open ChangeEngine and publish it to the workspace, or share it with the people who need it.",
    role,
  },
];

const chatgptTeam: SetupStep[] = [
  {
    text: "Everyone else selects Plugins in the sidebar, finds ChangeEngine in the workspace tab or opens the shared plugin link, then installs it and completes any sign-in prompts.",
  },
  { text: "Start a new chat and type @ChangeEngine at the start of your request." },
];

export const ASSISTANTS: AssistantInfo[] = [
  {
    id: "claude",
    name: "Claude",
    plan: "Free, Pro, Max, Team or Enterprise",
    logo: "https://images.changeengine.com/claude.webp",
    removePath:
      "Settings › Connectors › ChangeEngine › Disconnect (on Team or Enterprise, an Owner removes it in Organization settings › Connectors)",
    paths: [
      {
        id: "pro_max",
        label: "Free, Pro or Max",
        steps: [
          { text: "In Claude, click your name in the bottom-left corner, then click Settings." },
          { text: "Under Customize, click Connectors." },
          { text: "Click + Add, then choose Add custom connector." },
          { text: "Enter these details, then click Continue.", fields: claudeDetails },
          {
            text: "Sign in with your ChangeEngine account and click Allow. If Claude shows sign-in settings first, keep the ones it detected and click Add.",
          },
          {
            text: "Open ChangeEngine in your connectors list. Under Tool permissions, set Read-only tools to Always allow, so Claude doesn’t ask before every lookup.",
            fields: claudeToolPermissions,
          },
          {
            text: "Set the other tool groups to Always allow too if you don’t want to approve each draft Claude creates. You can change this at any time.",
          },
          { text: claudeStartChat },
        ],
      },
      {
        id: "team_enterprise",
        label: "Team or Enterprise",
        steps: [
          {
            text: "An Owner opens Organization settings › Connectors and clicks Add.",
            role: "Admin",
          },
          { text: "Hover over Custom, then choose Web.", role: "Admin" },
          {
            text: "Enter these details, then click Continue.",
            fields: claudeDetails,
            role: "Admin",
          },
          {
            text: "If Claude shows sign-in settings, keep the ones it detected and click Add.",
            role: "Admin",
          },
          {
            text: "Everyone else clicks their name in the bottom-left corner, opens Settings › Connectors, finds ChangeEngine and clicks Connect.",
          },
          { text: "Sign in with your ChangeEngine account and click Allow." },
          {
            text: "Open ChangeEngine in your connectors list. Under Tool permissions, set Read-only tools to Always allow, and the other tool groups too if you don’t want to approve each draft.",
            fields: claudeToolPermissions,
          },
          { text: claudeStartChat },
        ],
      },
    ],
    tryIt:
      "Open Claude, check ChangeEngine is switched on under + › Connectors, and paste one of these. Each one mentions ChangeEngine so Claude knows to use it.",
    prompt: ({ claude }) => claude,
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    plan: "ChatGPT Business or Enterprise",
    logo: "https://images.changeengine.com/chatgpt.webp",
    removePath:
      "Plugins in the sidebar › ChangeEngine (a workspace admin removes a published plugin from the workspace)",
    paths: [
      {
        id: "business",
        label: "Business",
        steps: [
          {
            text: "A workspace admin or owner selects Plugins in the sidebar (chatgpt.com/plugins on the web), clicks Add, then chooses Add custom MCP server. On Business, only admins and owners can add custom MCP servers.",
            role: "Admin",
          },
          ...chatgptCreatePlugin("Admin"),
          ...chatgptTeam,
        ],
      },
      {
        id: "enterprise",
        label: "Enterprise",
        steps: [
          {
            text: "An admin either sets this up themselves or, in Workspace settings › Permissions & roles, gives the person setting it up these permissions: Create plugins with MCPs, Share plugins and Publish plugins to workspace.",
            role: "Admin",
          },
          {
            text: "Select Plugins in the sidebar (chatgpt.com/plugins on the web), click Add, then choose Add custom MCP server.",
            role: "Setup owner",
          },
          ...chatgptCreatePlugin("Setup owner"),
          ...chatgptTeam,
        ],
      },
    ],
    tryIt:
      "Start a new chat in ChatGPT and paste one of these. Each one starts with @ChangeEngine so ChatGPT uses it.",
    prompt: ({ text }) => `@ChangeEngine ${text.charAt(0).toLowerCase()}${text.slice(1)}`,
  },
  {
    id: "copilot",
    name: "Microsoft Copilot",
    plan: "Microsoft 365 Copilot with Copilot Studio",
    logo: "https://images.changeengine.com/copilot.webp",
    removePath: "the agent’s Tools in Copilot Studio",
    paths: [
      {
        id: "copilot_studio",
        label: "Copilot Studio",
        steps: [
          {
            text: "Sign in to Copilot Studio and create or open an agent, for example “Comms assistant”.",
            role: "Admin",
          },
          {
            text: "Open Tools, then click Add a tool › New tool › Model Context Protocol.",
            role: "Admin",
          },
          {
            text: "Enter these details.",
            fields: [
              { label: "Server name", value: "ChangeEngine" },
              { label: "Server description", value: description },
              { label: "Server URL", connectionUrl: true },
            ],
            role: "Admin",
          },
          {
            text: "Choose this authentication, then click Create.",
            fields: [
              { label: "Authentication", value: "OAuth 2.0" },
              { label: "Type", value: "Dynamic discovery" },
            ],
            role: "Admin",
          },
          {
            text: "Select Create a new connection, sign in with your ChangeEngine account, then click Add to agent.",
            role: "Admin",
          },
          {
            text: "Click Publish. Then open Channels › Teams and Microsoft 365 Copilot, keep Make agent available in Microsoft 365 Copilot selected and click Add channel.",
            role: "Admin",
          },
          {
            text: "To share it with everyone, open Availability options › Show to everyone in my org and submit it for admin approval.",
            role: "Admin",
          },
          {
            text: "Your team opens Microsoft 365 Copilot, types @, picks the agent and starts asking.",
          },
        ],
      },
    ],
    tryIt: "Open Microsoft 365 Copilot, pick your ChangeEngine agent and paste one of these.",
    prompt: ({ text }) => text,
  },
];

export const assistantInfo = (assistant: Assistant): AssistantInfo =>
  ASSISTANTS.find(({ id }) => id === assistant)!;

const DATA_SHARING_AGREEMENT = [
  "By clicking Continue, you agree that ChangeEngine will share data from your organization’s ChangeEngine account with the AI assistants your team connects, starting with {tool}. You also confirm you’re authorized to accept this on your organization’s behalf.",
  "You only agree once. It covers your whole organization and every assistant, so teammates won’t be asked again.",
  "Your organization is the data controller for anything shared with an AI assistant, and that assistant’s own terms apply to it. ChangeEngine isn’t responsible for how {tool} or any other assistant handles that data.",
];

export const dataSharingAgreement = (assistant: Assistant): string[] =>
  DATA_SHARING_AGREEMENT.map((paragraph) =>
    paragraph.replaceAll("{tool}", assistantInfo(assistant).name),
  );

export const STARTER_PROMPTS: StarterPrompt[] = [
  {
    id: "create_comms",
    kind: "Create comms",
    text: "Draft an email and a matching Slack post announcing our new hybrid-working policy, scheduled for 9am Tuesday.",
    claude:
      "Draft an email and a matching Slack post in ChangeEngine announcing our new hybrid-working policy, scheduled for 9am Tuesday.",
  },
  {
    id: "build_program",
    kind: "Build a program",
    text: "Build a new-starter onboarding program around start date, with a manager prep email and a 30-day survey.",
    claude:
      "Build a new-starter onboarding program in ChangeEngine around start date, with a manager prep email and a 30-day survey.",
  },
  {
    id: "calendar",
    kind: "Check the calendar",
    text: "What’s scheduled to go out next week, and does anything clash?",
    claude: "Look in ChangeEngine: what’s scheduled to go out next week, and does anything clash?",
  },
  {
    id: "chain",
    kind: "Chain your tools",
    text: "Read our time-off policy in Google Drive and turn it into an explainer email for new joiners.",
    claude:
      "Read our time-off policy in Google Drive and turn it into an explainer email for new joiners as a ChangeEngine draft.",
  },
];
